/**
 * Module dependencies.
 */

import { Card, DatePicker } from 'antd';
import { Title } from 'app/components/atoms/title/title';
import { Loading } from 'app/components/atoms/loading/loading';
import { StatsItem } from 'app/components/organisms/stats-item/stats-item';
import { useGetUsersMetrics } from 'app/hooks/app-requests/metrics/use-get-users-metrics';
import moment, { Moment } from 'moment';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './users-metrics.module.less';
import { useSearchParams } from 'react-router-dom';

/**
 * Backoffice access types.
 */

const backOfficeAccessTypes = ['backOffice', 'admin'];

/**
 * Export `UsersMetrics` component.
 */

export function UsersMetrics(): JSX.Element {
  const [translate] = useTranslation();
  const [search, setSearch] = useSearchParams();

  const bounds = useMemo(() => {
    const from = search.get('from');
    const to = search.get('to');

    return {
      from: (from ? moment(from) : moment().subtract(1, 'month')).startOf('day'),
      to: (to ? moment(to) : moment()).endOf('day')
    };
  }, [search]);

  const churnFrom = useMemo(() => {
    const val = search.get('churnFrom');

    return val ? moment(val) : null;
  }, [search]);

  const updateBounds = useCallback(
    (key: 'from' | 'to' | 'churnFrom', value: Moment | null) => {
      setSearch(prev => {
        const next = new URLSearchParams(prev);

        if (value) {
          next.set(key, value.toISOString());
        } else {
          next.delete(key);
        }

        return next;
      });
    },
    [setSearch]
  );

  const fromDate = bounds.from.toISOString();
  const toDate = bounds.to.toISOString();

  const { data: mobileData, isLoading: isMobileLoading } = useGetUsersMetrics(fromDate, toDate, {
    churnFrom: search.get('churnFrom') ?? undefined,
    requireLastSeen: true
  });

  const { data: backOfficeData, isLoading: isBackOfficeLoading } = useGetUsersMetrics(fromDate, toDate, {
    accessType: backOfficeAccessTypes,
    churnFrom: search.get('churnFrom') ?? undefined,
    requireLastSeen: false
  });

  const churnFromDefault = useMemo(() => {
    return moment(bounds.from).subtract(bounds.to.diff(bounds.from, 'days'), 'days');
  }, [bounds.from, bounds.to]);

  return (
    <div className={styles.container}>
      <Title>{translate('users.metrics.title')}</Title>

      <Card className={styles.dateRangeCard}>
        <div className={styles.dateRange}>
          <div className={styles.datePicker}>
            <span className={styles.dateLabel}>{translate('common.labels.startDate')}</span>

            <DatePicker
              allowClear={false}
              disabledDate={current => (bounds.to ? current.isAfter(bounds.to, 'day') : false)}
              onChange={value => {
                updateBounds('from', value);
                if (value && churnFrom?.isSameOrAfter(value)) {
                  updateBounds('churnFrom', null);
                }
              }}
              value={bounds.from}
            />
          </div>

          <div className={styles.datePicker}>
            <span className={styles.dateLabel}>{translate('common.labels.endDate')}</span>

            <DatePicker
              allowClear={false}
              disabledDate={current => (bounds.from ? current.isBefore(bounds.from, 'day') : false)}
              onChange={value => updateBounds('to', value)}
              value={bounds.to}
            />
          </div>

          <div className={styles.datePicker}>
            <span className={styles.dateLabel}>
              {translate('common.labels.inactivitySince', { default: churnFromDefault.format('YYYY-MM-DD') })}
            </span>

            <DatePicker
              defaultPickerValue={bounds.from}
              disabledDate={current => (bounds.from ? current.isSameOrAfter(bounds.from, 'day') : false)}
              onChange={value => updateBounds('churnFrom', value ? value.startOf('day') : null)}
              value={churnFrom}
            />
          </div>
        </div>
      </Card>

      <Loading isLoading={isMobileLoading}>
        {mobileData && (
          <Card className={styles.metricsCard} title={translate('users.metrics.sections.mobile')}>
            <div>
              <StatsItem label={translate('users.metrics.newUsers')} value={String(mobileData.newUsersCount)} />

              <StatsItem label={translate('users.metrics.activeUsers')} value={String(mobileData.activeUsersCount)} />

              <StatsItem label={translate('users.metrics.churnedUsers')} value={String(mobileData.churnedUsersCount)} />

              <StatsItem
                label={translate('users.metrics.retentionRate')}
                value={mobileData.retentionRate !== null ? `${mobileData.retentionRate.toFixed(1)}%` : 'N/A'}
              />
            </div>

            <div className={styles.cardTitle}>{translate('users.metrics.subsections.therapies')}</div>

            <div>
              <StatsItem
                label={translate('users.metrics.usersWithActiveTherapies')}
                value={String(mobileData.usersWithActiveTherapiesCount)}
              />

              <StatsItem label={translate('users.metrics.newTherapies')} value={String(mobileData.newTherapiesCount)} />

              <StatsItem
                label={translate('users.metrics.averageTherapyDuration')}
                value={
                  mobileData.averageTherapyDurationDays !== null
                    ? translate('users.metrics.averageTherapyDurationValue', {
                        days: mobileData.averageTherapyDurationDays
                      })
                    : 'N/A'
                }
              />
            </div>
          </Card>
        )}
      </Loading>

      <Loading isLoading={isBackOfficeLoading}>
        {backOfficeData && (
          <Card className={styles.metricsCard} title={translate('users.metrics.sections.backOffice')}>
            <StatsItem label={translate('users.metrics.newUsers')} value={String(backOfficeData.newUsersCount)} />

            <StatsItem label={translate('users.metrics.activeUsers')} value={String(backOfficeData.activeUsersCount)} />

            <StatsItem
              label={translate('users.metrics.churnedUsers')}
              value={String(backOfficeData.churnedUsersCount)}
            />
          </Card>
        )}
      </Loading>
    </div>
  );
}
